import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  InputAdornment,
  Chip,
  Link,
  Stack,
  Switch,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { fetchData } from '../../helpers/apiUtils';
import { formatGermanTime } from '../../helpers/germanTime';
import { useUrlValidation } from '../../hooks/useUrlValidation';
import { UserContext } from '../../contexts/UserContext';
import { hasPublicTitle, publicTitleRequired } from './publicTitle';
import { Abschnitt, BEGRIFF, Feld, NurAdmin, useFeldpruefung } from '../../components/marketingForm';

/** Der LP-Manager schreibt genau dieses Praefix; alles andere ist PL-X-Altbestand oder Fremd-URL. */
const LP_URL_PREFIX = 'https://lp.finanztrends.de/';

function urlOriginChip(value: string): React.ReactElement {
  if (!value) return <Chip size="small" color="warning" label="noch nicht veröffentlicht" />;
  if (value.startsWith(LP_URL_PREFIX)) return <Chip size="small" color="success" label="live" />;
  return <Chip size="small" label="alte Seite (PL-X)" />;
}

/** Nur-Lese-Zeile einer LP-URL: Wert monospace + anklickbar, daneben die Herkunft. */
const UrlReadOnlyRow: React.FC<{ label: string; value: string; hint: string }> = ({
  label,
  value,
  hint,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 0.75,
      borderBottom: '1px solid #eeeeee',
    }}
  >
    <Typography variant="body2" sx={{ minWidth: 200, color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {value ? (
        <Link
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
        >
          {value}
        </Link>
      ) : (
        <Typography variant="body2" color="text.disabled" fontFamily="monospace">
          —
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        {hint}
      </Typography>
    </Box>
    {urlOriginChip(value)}
  </Box>
);

interface Webinar {
  webinar_id: string;
  /** INTERNER Arbeitstitel — nur im Dashboard, nie oeffentlich. */
  title: string;
  /** Oeffentlicher Titel; seit 2026-10-02 Pflicht fuer jeden aktiven kommenden Termin. */
  display_title?: string | null;
  plx_product: string | null;
  plx_product_name: string | null;
  concept: string | null;
  timezone_id: string;
  start_date: string;
  type: 'automated' | 'live' | 'hybrid' | 'replay';
  is_evergreen: boolean | number;
  evergreen_frequency: string | null;
  registration_page_id: string | null;
  signup_url: string | null;
  doi_url: string | null;
  missed_webinar_url: string | null;
  mc_reminder_list_id: string | null;
  is_recurring?: boolean | number;
  recurrence_days?: number[] | null;
  recurrence_time?: string | null;
  next_occurrence_date?: string | null;
  host_name?: string | null;
  /** Killswitch; fehlt er, gilt der Spalten-Default 1. */
  active?: boolean | number;
  /** „Aufzeichnung verfügbar" (03.11.2026): NULL = nicht gesetzt (Altbestand). */
  recording_availability?: 'hours' | 'forever' | null;
  recording_available_hours?: number | null;
}

type AufzeichnungEinheit = 'hours' | 'days' | 'forever' | '';

/** Gespeicherte Spalten → Formularwerte; volle Tage als Tage, sonst Stunden. */
function aufzeichnungAusWebinar(w: Webinar | null): { einheit: AufzeichnungEinheit; anzahl: string } {
  if (w?.recording_availability === 'forever') return { einheit: 'forever', anzahl: '' };
  const h = Number(w?.recording_available_hours);
  if (w?.recording_availability === 'hours' && Number.isInteger(h) && h > 0) {
    return h % 24 === 0 ? { einheit: 'days', anzahl: String(h / 24) } : { einheit: 'hours', anzahl: String(h) };
  }
  return { einheit: '', anzahl: '' };
}

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** Berliner Wandzeit `YYYY-MM-DD HH:mm:ss` → „Di 10.11., 21:00" (nur Anzeige, keine Zeitzonenrechnung). */
function bisText(wallclock: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(wallclock);
  if (!m) return wallclock;
  const tag = WOCHENTAG[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
  return `${tag} ${m[3]}.${m[2]}., ${m[4]}:${m[5]}`;
}

type AufzeichnungVorschau =
  | { kind: 'unset' | 'forever' }
  | { kind: 'until'; untilWallclock: string; endEstimated: boolean }
  | { kind: 'invalid'; reason: string };

interface MailchimpList {
  id: string;
  name: string;
}

interface Product {
  plx_product: string;
  name: string;
}

interface WebinarFormProps {
  webinar: Webinar | null; // null = new webinar, otherwise edit mode
  /** `createdWebinarId` nur beim Anlegen: die vom Server erzeugte ID (Hinweis "Als Naechstes"). */
  onSaved: (warning?: string, createdWebinarId?: string) => void;
  onCancel: () => void;
}

/** Vorschau-Muster aus den Reminder-Mails (Betreff Tag X, Absendername) — nur Anzeige. */
const BETREFF_VORSCHAU = 'Heute live: ';
const ABSENDER_VORSCHAU = 'Webinarleitung | ';

const WebinarForm: React.FC<WebinarFormProps> = ({ webinar, onSaved, onCancel }) => {
  const isEditMode = webinar !== null;
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const isAdmin = !!user?.is_admin;

  // Die drei LP-URLs schreibt der LP-Manager; das Formular zeigt sie nur. Der Server lehnt die
  // Felder fuer Nicht-Admins mit 403 ab, also duerfen sie NUR mit aktivem Override in den Body.
  const [adminOverride, setAdminOverride] = useState(false);
  const urlFieldsEditable = isAdmin && adminOverride;

  // Die Checkliste „Bewerbbar?“ steht seit 2026-10-22 im Webinar-Fahrplan (WebinarFahrplan.tsx), nicht mehr hier.
  // Feldpruefung des oeffentlichen Titels (Regeln + KI, nur Hinweis) — braucht eine Webinar-ID, also nur beim Bearbeiten.
  const titelPruefung = useFeldpruefung('webinar.display_title', webinar?.webinar_id);

  // Form state. Anlegen geht seit 2026-10-02 ohne StealthSeminar: die ID erzeugt der Server, Typ und
  // Termin-Liste setzt er selbst; das Formular liefert Start (Berlin), Titel und Stammdaten.
  const [title, setTitle] = useState(webinar?.title || '');
  // Oeffentlicher Titel — Pflicht fuer kommende Termine (Server-Sperre auf dem Zielzustand).
  const [displayTitle, setDisplayTitle] = useState(webinar?.display_title || '');
  const publicTitleRef = useRef<HTMLInputElement | null>(null);
  // Nur Anlegen: Start als Berlin-Wallclock, getrennt nach Datum und Uhrzeit.
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('18:00');
  const [concept, setConcept] = useState(webinar?.concept || '');
  const [plxProduct, setPlxProduct] = useState(webinar?.plx_product || '');
  const [doiUrl, setDoiUrl] = useState(webinar?.doi_url || '');
  const [signupUrl, setSignupUrl] = useState(webinar?.signup_url || '');
  const [missedWebinarUrl, setMissedWebinarUrl] = useState(webinar?.missed_webinar_url || '');
  const [mcReminderListId, setMcReminderListId] = useState(webinar?.mc_reminder_list_id || '');

  // Newsletter ad fields
  const [hostName, setHostName] = useState(webinar?.host_name || '');

  // „Aufzeichnung verfügbar" — Pflicht beim Anlegen und Bearbeiten (Till 03.11.); liest Nachfass + KI-Redlinks.
  const [aufzEinheit, setAufzEinheit] = useState<AufzeichnungEinheit>(() => aufzeichnungAusWebinar(webinar).einheit);
  const [aufzAnzahl, setAufzAnzahl] = useState(() => aufzeichnungAusWebinar(webinar).anzahl);
  const [aufzVorschau, setAufzVorschau] = useState<AufzeichnungVorschau | null>(null);
  const aufzAnzahlGueltig = /^\d{1,5}$/.test(aufzAnzahl.trim()) && Number(aufzAnzahl) > 0;
  const aufzFehlt = aufzEinheit === '' || (aufzEinheit !== 'forever' && !aufzAnzahlGueltig);

  // Products dropdown
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Mailchimp lists dropdown
  const [mailchimpLists, setMailchimpLists] = useState<MailchimpList[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  // Pflicht-Regel des Servers (helpers/webinarPublicTitle.ts) auf dem Zustand NACH dem Speichern:
  // Anlegen = immer kommender Termin; Bearbeiten = Bestand. Evergreen/Serie sind seit 2026-10-30 nicht
  // mehr einstellbar (0 Zeilen je genutzt); ein Altbestand behaelt seine Werte, das Formular schickt sie nicht.
  const titleRequired = isEditMode
    ? publicTitleRequired({
        active: webinar.active,
        is_evergreen: webinar.is_evergreen,
        is_recurring: webinar.is_recurring,
        start_date: webinar.start_date,
        next_occurrence_date: webinar.is_recurring ? webinar.next_occurrence_date : null,
      })
    : true;
  const publicTitlePreview = displayTitle.trim();

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL validation
  const { validateUrl, getState, resetField, hasUnreachableUrls, isValidating } =
    useUrlValidation();
  const doiUrlState = getState('doi_url');
  const signupUrlState = getState('signup_url');
  const missedWebinarUrlState = getState('missed_webinar_url');

  /**
   * Override aus: Werte auf den gespeicherten Stand zurueck UND den Validierungszustand leeren.
   * Sonst haelt ein vorher getippter unerreichbarer Wert `hasUnreachableUrls()` auf true und der
   * Speichern-Knopf bleibt ohne sichtbaren Grund tot (react-frontend.md § Feld, dessen Fehler du
   * versteckst).
   */
  const toggleAdminOverride = (on: boolean) => {
    setAdminOverride(on);
    if (on) return;
    setDoiUrl(webinar?.doi_url || '');
    setSignupUrl(webinar?.signup_url || '');
    setMissedWebinarUrl(webinar?.missed_webinar_url || '');
    resetField('doi_url');
    resetField('signup_url');
    resetField('missed_webinar_url');
  };

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetchData<Product[]>('/api/webinar-management/products');
        if (response.success && response.data) {
          setProducts(response.data);
        }
      } catch (err) {
        console.error('Failed to load products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Load Mailchimp lists on mount
  useEffect(() => {
    const loadMailchimpLists = async () => {
      try {
        const response = await fetchData<MailchimpList[]>(
          '/api/webinar-management/mailchimp-lists',
        );
        if (response.success && response.data) {
          setMailchimpLists(response.data);
        }
      } catch (err) {
        console.error('Failed to load Mailchimp lists:', err);
      } finally {
        setLoadingLists(false);
      }
    };
    loadMailchimpLists();
  }, []);

  // „verfügbar bis" vom Server (dieselbe Regel wie Nachfass/KI-Redlinks), entprellt; Antwort zu alter Eingabe verwerfen.
  const vorschauWebinarId = webinar?.webinar_id ?? '';
  useEffect(() => {
    setAufzVorschau(null);
    if (aufzFehlt) return;
    const start = `${startDate} ${startTime}`;
    if (!vorschauWebinarId && !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(start)) return;
    let abgebrochen = false;
    const t = setTimeout(async () => {
      const q = new URLSearchParams({ unit: aufzEinheit });
      if (aufzEinheit !== 'forever') q.set('amount', aufzAnzahl.trim());
      if (vorschauWebinarId) q.set('webinar_id', vorschauWebinarId);
      else q.set('start', start);
      const r = await fetchData<AufzeichnungVorschau>(`/api/webinar-management/recording-availability-preview?${q}`);
      if (!abgebrochen && r.success && r.data) setAufzVorschau(r.data);
    }, 400);
    return () => {
      abgebrochen = true;
      clearTimeout(t);
    };
  }, [aufzEinheit, aufzAnzahl, aufzFehlt, startDate, startTime, vorschauWebinarId]);

  // Save webinar (create or update)
  const handleSave = async () => {
    setError(null);

    // Validation — spiegelt den Server (routes/webinar-management.ts), der bleibt massgeblich.
    if (titleRequired && !hasPublicTitle(displayTitle)) {
      setError(
        'Öffentlicher Titel fehlt — ohne ihn sähe jeder Anmelder den internen Arbeitstitel ' +
          '(Seiten, Mails samt Betreff und Absender, Kalender, Portale, Raum, Anzeigen).',
      );
      publicTitleRef.current?.focus();
      return;
    }

    if (!title.trim()) {
      setError('Interner Titel ist erforderlich');
      return;
    }

    if (!isEditMode) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{2}:\d{2}$/.test(startTime)) {
        setError('Start (Datum und Uhrzeit, Berliner Zeit) ist erforderlich');
        return;
      }
      if (!plxProduct) {
        setError('PLX Produkt ist erforderlich');
        return;
      }
      if (!hostName.trim()) {
        setError('Referent ist erforderlich');
        return;
      }
    }

    if (!concept.trim()) {
      setError('Concept ist erforderlich');
      return;
    }

    if (concept.length > 10) {
      setError('Concept darf maximal 10 Zeichen lang sein');
      return;
    }

    if (aufzFehlt) {
      setError('„Aufzeichnung verfügbar" fehlt — bitte Stunden, Tage oder „für immer" angeben.');
      return;
    }
    const recordingAvailable =
      aufzEinheit === 'forever' ? { unit: 'forever' } : { unit: aufzEinheit, amount: Number(aufzAnzahl.trim()) };

    // Mailchimp-Liste ist seit 2026-10-15 optional: die Erinnerungen gehen über SES.

    setSaving(true);

    try {
      let response;

      // LP-URLs NUR mit Admin-Override — sonst die Schluessel komplett weglassen (nicht null
      // schicken): der Server antwortet auf jedes Vorkommen der Felder ohne Admin mit 403.
      const urlPayload = urlFieldsEditable
        ? {
            doi_url: doiUrl || null,
            signup_url: signupUrl || null,
            missed_webinar_url: missedWebinarUrl || null,
          }
        : {};

      let createdWebinarId: string | undefined;
      if (isEditMode) {
        // Update existing webinar
        response = await fetchData(`/api/webinar-management/webinars/${webinar!.webinar_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title,
            display_title: displayTitle.trim() || null,
            concept,
            plx_product: plxProduct || null,
            ...urlPayload,
            mc_reminder_list_id: mcReminderListId || null,
            host_name: hostName || null,
            recording_available: recordingAvailable,
          }),
        });
      } else {
        // Anlegen im eigenen Raum: KEINE webinar_id — der Server erzeugt sie, setzt Typ
        // 'automated', upcoming_times und Zeitzone. Nur Einzeltermine (Evergreen/Serie entfernt 2026-10-30).
        const created = await fetchData<{ webinar_id: string }>(
          '/api/webinar-management/webinars',
          {
            method: 'POST',
            body: JSON.stringify({
              title: title.trim(),
              display_title: displayTitle.trim(),
              concept,
              start_date: `${startDate} ${startTime}`,
              plx_product: plxProduct,
              ...urlPayload,
              mc_reminder_list_id: mcReminderListId || null,
              host_name: hostName.trim(),
              recording_available: recordingAvailable,
            }),
          },
        );
        createdWebinarId = created.data?.webinar_id;
        response = created;
      }

      if (response.success) {
        onSaved((response as { warning?: string }).warning, createdWebinarId);
      } else {
        setError(response.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setError('Speichern fehlgeschlagen');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isEditMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Hier legst du das Webinar mit seinen Grunddaten an. Danach führt dich der{' '}
          <strong>Fahrplan</strong> Schritt für Schritt durch den Rest: Anmelde- und Bestätigungsseite,
          Erinnerungsmails, Video und Kaufangebot im Raum, Bestellstrecke.
        </Alert>
      )}

      {isEditMode && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <Typography variant="body2" color="text.secondary">
            Webinar-ID <strong style={{ fontFamily: 'monospace' }}>{webinar.webinar_id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Termin <strong>{formatGermanTime(webinar.start_date, 'DD.MM.YYYY HH:mm')} Uhr</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Den Termin kann man nach dem Anlegen hier nicht ändern — bei einer Verschiebung bitte den
            Admin (Till) fragen.
          </Typography>
        </Box>
      )}

      <Abschnitt
        titel="Grunddaten"
        erklaerung="Was Anmelder sehen und wie das Webinar intern heißt. Alles in diesem Abschnitt ist Pflicht."
      >
        <Feld
          titel={BEGRIFF.oeffentlicherTitel}
          art="pflicht"
          wofuer="Sehen alle Anmelder: Anmelde-, Bestätigungs- und Aufzeichnungsseite, Browser-Tab, alle Mails samt Betreff und Absender, Kalendereintrag, Portal-Werbung, Raum, Anzeigen."
          befunde={titelPruefung.befunde}
          pruefungLaeuft={titelPruefung.laeuft}
        >
          <Box
            sx={{ p: 1.5, border: '2px solid #4cceac', borderRadius: 1, backgroundColor: '#eefaf6' }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="z. B. Der Robo-Code"
              value={displayTitle}
              onChange={(e) => setDisplayTitle(e.target.value)}
              onBlur={() => void titelPruefung.pruefen(displayTitle, { mitKi: true })}
              inputRef={publicTitleRef}
              required={titleRequired}
              error={titleRequired && !hasPublicTitle(displayTitle)}
              sx={{ backgroundColor: '#ffffff' }}
            />
            <Box sx={{ mt: 1, pl: 0.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                So sieht es in den Mails aus:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Betreff: {BETREFF_VORSCHAU}
                {publicTitlePreview || <em>(öffentlicher Titel fehlt)</em>}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Absender: {ABSENDER_VORSCHAU}
                {publicTitlePreview || <em>(öffentlicher Titel fehlt)</em>}
              </Typography>
            </Box>
          </Box>
        </Feld>

        <Feld
          titel={BEGRIFF.internerTitel}
          art="pflicht"
          wofuer="Arbeitstitel nur fürs Dashboard (Listen, Auswertungen) — sieht nie ein Kunde."
        >
          <TextField
            fullWidth
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
          />
        </Feld>

        {!isEditMode && (
          <Feld
            titel="Termin"
            art="pflicht"
            wofuer="Datum und Uhrzeit des Webinars, deutsche Zeit — Sommer-/Winterzeit rechnet das System. Bitte genau prüfen: nach dem Anlegen ändert den Termin nur noch der Admin (Till)."
          >
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                size="small"
                label="Datum"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                size="small"
                label="Uhrzeit"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Feld>
        )}

        <Feld
          titel="Aufzeichnung verfügbar"
          art="pflicht"
          wofuer="Wie lange die Aufzeichnung nach dem Ende des Webinars online bleibt. Daraus nennen Nachfass-Mails und KI-Texte die Frist. Abschalten musst du die Aufzeichnung weiterhin selbst."
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {aufzEinheit !== 'forever' && (
              <TextField
                size="small"
                label="Anzahl"
                value={aufzAnzahl}
                onChange={(e) => setAufzAnzahl(e.target.value)}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Aufzeichnung verfügbar Anzahl' }}
                error={aufzEinheit !== '' && !aufzAnzahlGueltig}
                sx={{ width: 110 }}
              />
            )}
            <FormControl size="small" required error={aufzEinheit === ''} sx={{ minWidth: 160 }}>
              <Select
                value={aufzEinheit}
                onChange={(e) => setAufzEinheit(e.target.value as AufzeichnungEinheit)}
                displayEmpty
                inputProps={{ 'aria-label': 'Aufzeichnung verfügbar Einheit' }}
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                <MenuItem value="hours">Stunden</MenuItem>
                <MenuItem value="days">Tage</MenuItem>
                <MenuItem value="forever">für immer</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="aufzeichnung-vorschau">
            {aufzEinheit === ''
              ? isEditMode
                ? 'Noch nicht eingetragen — die Mails rechnen bis dahin mit 48 Stunden und nennen keine Frist.'
                : 'Bitte angeben.'
              : aufzVorschau?.kind === 'forever'
                ? 'Die Aufzeichnung bleibt dauerhaft verfügbar — in den Mails steht keine Frist.'
                : aufzVorschau?.kind === 'until'
                  ? aufzVorschau.endEstimated
                    ? `Verfügbar bis ca. ${bisText(aufzVorschau.untilWallclock)} Uhr — gerechnet ab Start, bis das Video im Raum hochgeladen ist (dann ab Videoende).`
                    : `Verfügbar bis ${bisText(aufzVorschau.untilWallclock)} Uhr (ab Ende des Videos im Raum).`
                  : ''}
          </Typography>
        </Feld>

        <Feld
          titel={BEGRIFF.produkt}
          art="pflicht"
          wofuer="Welches Abo im Webinar verkauft wird. Davon hängen die Zuordnung der Bestellungen, die Produkt-Bilder und die Prüfung der Bestellseite ab."
        >
          <FormControl fullWidth size="small" required={!isEditMode}>
            <Select
              value={plxProduct}
              onChange={(e) => setPlxProduct(e.target.value)}
              disabled={loadingProducts}
              displayEmpty
            >
              <MenuItem value="">
                <em>Bitte wählen</em>
              </MenuItem>
              {products.map((product) => (
                <MenuItem key={product.plx_product} value={product.plx_product}>
                  {product.name} ({product.plx_product})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Feld>

        <Feld
          titel="Referent"
          art="pflicht"
          wofuer="Name, wie er auf den Seiten und in den Mails steht."
          beispiele={
            isEditMode
              ? { feld: 'webinar.host_name', webinarId: webinar.webinar_id, onUebernehmen: setHostName }
              : undefined
          }
        >
          <TextField
            fullWidth
            size="small"
            placeholder="z. B. Carsten Müller"
            value={hostName}
            onChange={(e) => setHostName(e.target.value)}
            required={!isEditMode}
          />
        </Feld>

        <Feld
          titel={BEGRIFF.kuerzel}
          art="pflicht"
          wofuer="Kurzes Kennwort für Auswertungen und Tracking-Links, 1–10 Zeichen ohne Leerzeichen, z. B. ROBO. Üblich: Produktkürzel + Thema (FUMDWC = Future Money, Watt-Code)."
          beispiele={
            isEditMode
              ? {
                  feld: 'webinar.concept',
                  webinarId: webinar.webinar_id,
                  onUebernehmen: (v) => setConcept(v.toUpperCase().replace(/\s/g, '').slice(0, 10)),
                  hinweis: 'Nur zur Orientierung — jedes Webinar braucht ein eigenes Kürzel.',
                }
              : undefined
          }
        >
          <TextField
            size="small"
            value={concept}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().replace(/\s/g, '');
              if (value.length <= 10) {
                setConcept(value);
              }
            }}
            required
            inputProps={{ maxLength: 10 }}
            sx={{ width: 220 }}
          />
        </Feld>
      </Abschnitt>

      {isEditMode && (
        <Abschnitt
          titel="Adressen der Seiten"
          art="automatisch"
          eingeklappt
          erklaerung="Werden automatisch gesetzt, wenn ihr eine Seite unter „Webinar LPs“ veröffentlicht. Hier nur zum Nachsehen."
        >
          <UrlReadOnlyRow label="Anmeldeseite" value={signupUrl} hint="Ziel aller Werbung für dieses Webinar" />
          <UrlReadOnlyRow label="Bestätigungsseite" value={doiUrl} hint="Erscheint nach der Bestätigung der E-Mail" />
          <UrlReadOnlyRow
            label="Aufzeichnungsseite"
            value={missedWebinarUrl}
            hint="Späte Anmelder und Nachfass-Mails führen hierher"
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate('/webinar-lp')}
            sx={{ mt: 1.5 }}
          >
            Webinar LPs öffnen
          </Button>
        </Abschnitt>
      )}

      <NurAdmin bereich>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Mailchimp-Liste (nur Altbestand)</InputLabel>
              <Select
                value={mcReminderListId}
                label="Mailchimp-Liste (nur Altbestand)"
                onChange={(e) => setMcReminderListId(e.target.value)}
                disabled={loadingLists}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>Keine — Erinnerungen gehen über SES</em>
                </MenuItem>
                {mailchimpLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={adminOverride}
                  onChange={(e) => toggleAdminOverride(e.target.checked)}
                />
              }
              label="Seiten-Adressen manuell überschreiben (PL-X-Altbestand, Rückweg)"
            />
          </Grid>
          {urlFieldsEditable && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="DOI URL"
                  placeholder="https://..."
                  value={doiUrl}
                  onChange={(e) => setDoiUrl(e.target.value)}
                  onBlur={() => validateUrl('doi_url', doiUrl)}
                  error={doiUrlState.reachable === false && !doiUrlState.cross_domain_redirect}
                  helperText={
                    doiUrlState.validating
                      ? 'URL wird geprüft...'
                      : doiUrlState.warning
                        ? doiUrlState.warning
                        : doiUrlState.reachable === true
                          ? 'URL erreichbar'
                          : doiUrlState.cross_domain_redirect
                            ? `Weiterleitung zu anderer Domain: ${doiUrlState.redirect_url}`
                            : doiUrlState.error
                              ? doiUrlState.error
                              : 'Bestätigungs-Seite nach Double-Opt-In'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {doiUrlState.validating && <CircularProgress size={18} />}
                        {doiUrlState.warning && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {doiUrlState.reachable === true &&
                          !doiUrlState.cross_domain_redirect &&
                          !doiUrlState.warning && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        {doiUrlState.cross_domain_redirect && !doiUrlState.warning && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {doiUrlState.reachable === false &&
                          !doiUrlState.cross_domain_redirect && (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Signup URL"
                  placeholder="https://..."
                  value={signupUrl}
                  onChange={(e) => setSignupUrl(e.target.value)}
                  onBlur={() => validateUrl('signup_url', signupUrl)}
                  error={
                    signupUrlState.reachable === false && !signupUrlState.cross_domain_redirect
                  }
                  helperText={
                    signupUrlState.validating
                      ? 'URL wird geprüft...'
                      : signupUrlState.warning
                        ? signupUrlState.warning
                        : signupUrlState.reachable === true
                          ? 'URL erreichbar'
                          : signupUrlState.cross_domain_redirect
                            ? `Weiterleitung zu anderer Domain: ${signupUrlState.redirect_url}`
                            : signupUrlState.error
                              ? signupUrlState.error
                              : 'Anmelde-Seite für das Webinar (Query-Parameter werden entfernt)'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {signupUrlState.validating && <CircularProgress size={18} />}
                        {signupUrlState.warning && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {signupUrlState.reachable === true &&
                          !signupUrlState.cross_domain_redirect &&
                          !signupUrlState.warning && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        {signupUrlState.cross_domain_redirect && !signupUrlState.warning && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {signupUrlState.reachable === false &&
                          !signupUrlState.cross_domain_redirect && (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Replay-URL (Aufzeichnung)"
                  placeholder="https://lpm.finanztrends.pl-x.de/.../-aufzeichnung/"
                  value={missedWebinarUrl}
                  onChange={(e) => setMissedWebinarUrl(e.target.value)}
                  onBlur={() => validateUrl('missed_webinar_url', missedWebinarUrl)}
                  error={
                    missedWebinarUrlState.reachable === false &&
                    !missedWebinarUrlState.cross_domain_redirect
                  }
                  helperText={
                    missedWebinarUrlState.validating
                      ? 'URL wird geprüft...'
                      : missedWebinarUrlState.warning
                        ? missedWebinarUrlState.warning
                        : missedWebinarUrlState.reachable === true
                          ? 'URL erreichbar'
                          : missedWebinarUrlState.cross_domain_redirect
                            ? `Weiterleitung zu anderer Domain: ${missedWebinarUrlState.redirect_url}`
                            : missedWebinarUrlState.error
                              ? missedWebinarUrlState.error
                              : 'Späte Anmelder werden hierhin weitergeleitet (Banner: …_MWRR).'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {missedWebinarUrlState.validating && <CircularProgress size={18} />}
                        {missedWebinarUrlState.warning && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {missedWebinarUrlState.reachable === true &&
                          !missedWebinarUrlState.cross_domain_redirect &&
                          !missedWebinarUrlState.warning && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        {missedWebinarUrlState.cross_domain_redirect &&
                          !missedWebinarUrlState.warning && (
                            <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                          )}
                        {missedWebinarUrlState.reachable === false &&
                          !missedWebinarUrlState.cross_domain_redirect && (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </NurAdmin>

      <Box mt={3} display="flex" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || hasUnreachableUrls() || isValidating()}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Speichert …' : isEditMode ? 'Speichern' : 'Webinar anlegen'}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={saving}>
          Abbrechen
        </Button>
      </Box>
    </Paper>
  );
};

export default WebinarForm;
